import React from "react";
import { Link } from "gatsby";
import { Facebook, Instagram, Mail, MapPin, Phone, Linkedin } from "lucide-react";
import { StaticImage } from "gatsby-plugin-image";

const TikTokIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const Footer = () => {
  return (
    <footer className="bg-[#1a1a1a] text-gray-400 py-16 border-t border-white/5">
      <div className="container mx-auto px-6 grid grid-cols-1 md:grid-cols-4 gap-12">
        {/* Brand Column */}
        <div className="space-y-6">
          <StaticImage
            src="../../images/logo.jpeg"
            alt="TAG Logo"
            className="h-24 w-24 rounded-xl"
            objectFit="contain"
            placeholder="blurred"
          />

          <p className="text-sm leading-relaxed max-w-xs">
            TAG is a premier sound engineering and audio production company in Sri Lanka, delivering world-class sonic
            experiences.
          </p>
          <div className="flex space-x-4 pt-2">
            <a
              href="https://www.instagram.com/ascension_lk/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#ffa248] transition-colors"
            >
              <Instagram size={20} />
            </a>
            <a
              href="https://www.linkedin.com/company/ascensionlk/?originalSubdomain=lk"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#ffa248] transition-colors"
            >
              <Linkedin size={20} />
            </a>
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#ffa248] transition-colors"
            >
              <Facebook size={20} />
            </a>
            <a
              href="https://tiktok.com"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#ffa248] transition-colors"
            >
              <TikTokIcon size={20} />
            </a>
          </div>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="text-white font-bold uppercase tracking-wider mb-6 text-sm">Company</h3>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/" className="hover:text-[#ffa248] transition-colors">
                Home
              </Link>
            </li>
            <li>
              <Link to="/about-us" className="hover:text-[#ffa248] transition-colors">
                About Us
              </Link>
            </li>
            <li>
              <Link to="/contact-us" className="hover:text-[#ffa248] transition-colors">
                Contact Us
              </Link>
            </li>
            <li>
              <Link to="/privacy-policy" className="hover:text-[#ffa248] transition-colors">
                Privacy Policy
              </Link>
            </li>
          </ul>
        </div>

        {/* Divisions */}
        <div>
          <h3 className="text-white font-bold uppercase tracking-wider mb-6 text-sm">Divisions</h3>
          <ul className="space-y-3 text-sm">
            <li>
              <Link to="/companies/crystal-sound" className="hover:text-[#ffa248] transition-colors">
                Crystal Sound
              </Link>
            </li>
            <li>
              <Link to="/companies/cf-production" className="hover:text-[#ffa248] transition-colors">
                CF Production
              </Link>
            </li>
            <li>
              <Link to="/companies/cf-events" className="hover:text-[#ffa248] transition-colors">
                CF Events
              </Link>
            </li>
            <li>
              <Link to="/companies/cf-entertainment" className="hover:text-[#ffa248] transition-colors">
                CF Entertainment
              </Link>
            </li>
            <li>
              <Link to="/companies/ascension" className="hover:text-[#ffa248] transition-colors">
                Ascension
              </Link>
            </li>
            <li>
              <Link to="/companies/members-only" className="hover:text-[#ffa248] transition-colors">
                Members Only
              </Link>
            </li>
            <li>
              <Link to="/companies/idea-prenuer" className="hover:text-[#ffa248] transition-colors">
                IdeaPrenuer
              </Link>
            </li>
            <li>
              <Link to="/companies/coordinates" className="hover:text-[#ffa248] transition-colors">
                Coordinates
              </Link>
            </li>
          </ul>
        </div>

        {/* Contact Info */}
        <div>
          <h3 className="text-white font-bold uppercase tracking-wider mb-6 text-sm">Contact</h3>
          <ul className="space-y-4 text-sm">
            <li className="flex items-start space-x-3">
              <MapPin size={18} className="text-[#ffa248] mt-1 shrink-0" />
              <span>
                19/6 Thelangapatha Rd,
                <br />
                Wattala 11300
              </span>
            </li>
            <li className="flex items-center space-x-3">
              <Phone size={18} className="text-[#ffa248] shrink-0" />
              <a href="tel:+94773729229" className="text-gray-400 hover:text-[#ffa248] transition-colors">
                077 372 9229
              </a>
            </li>
            <li className="flex items-center space-x-3">
              <Mail size={18} className="text-[#ffa248] shrink-0" />
              <a href="mailto:info@tag.lk" className="text-gray-400 hover:text-[#ffa248] transition-colors">
                info@tag.lk
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div className="container mx-auto px-6 mt-16 pt-8 border-t border-white/5 text-center text-xs text-gray-600">
        &copy; {new Date().getFullYear()} TAG.lk. All rights reserved.
      </div>
    </footer>
  );
};

export default Footer;
